/*
 * Cart Gift Wrap Manager — Cécile et Jeanne
 * Toggle checkbox → sync line item properties (_giftwrap, _giftwrap_message) + add/remove produit cadeau
 */

const CartGiftWrapManager = {
	config: {
		selectors: {
			block:    '[data-giftwrap-block]',
			toggle:   '[data-giftwrap-toggle]',
			body:     '[data-giftwrap-body]',
			textarea: '[data-giftwrap-textarea]',
		},
		debounceMs: 600,
	},

	state: {
		pendingUpdates: new Map(),
	},

	init() {
		this._bindEvents();
		window.log?.('info', '🎁 [CART_GIFTWRAP]', 'Initialized');
	},

	_bindEvents() {
		document.addEventListener('change', (e) => {
			if (e.target.matches(this.config.selectors.toggle)) this._handleToggleChange(e.target);
		});
		document.addEventListener('input', (e) => {
			if (e.target.matches(this.config.selectors.textarea)) this._handleTextareaInput(e.target);
		});
		document.addEventListener('cart:before-item-remove', (e) => {
			this._handleItemRemove(e.detail.data);
		});
	},

	_handleToggleChange(checkbox) {
		const lineIndex = checkbox.dataset.lineIndex;
		if (!lineIndex) return;
		const block = checkbox.closest(this.config.selectors.block);
		const body = block?.querySelector(this.config.selectors.body);
		const textarea = block?.querySelector(this.config.selectors.textarea);
		const giftVariantId = block?.dataset.giftVariantId;

		if (checkbox.checked) {
			if (body) body.hidden = false;
			/* Sync les properties d'abord, puis add le produit cadeau pour éviter
			   que le refreshDOM écrase l'état _giftwrap avant qu'il soit sauvé */
			this._sync(lineIndex, { _giftwrap: 'true', _giftwrap_message: textarea?.value || '' })
				.then(() => { if (giftVariantId) this._updateGiftProduct(giftVariantId, 1); });
		} else {
			if (body) body.hidden = true;
			this._sync(lineIndex, { _giftwrap: '', _giftwrap_message: '' });
			if (giftVariantId) this._updateGiftProduct(giftVariantId, 0);
		}
	},

	_handleItemRemove({ lineIndex }) {
		const itemEl = document.querySelector(`[data-line-index="${lineIndex}"]`);
		if (!itemEl) return;
		const checkbox = itemEl.querySelector(this.config.selectors.toggle);
		if (!checkbox?.checked) return;
		const giftVariantId = itemEl.querySelector(this.config.selectors.block)?.dataset.giftVariantId;
		if (giftVariantId) this._updateGiftProduct(giftVariantId, 0);
	},

	_handleTextareaInput(textarea) {
		const lineIndex = textarea.dataset.lineIndex;
		if (!lineIndex) return;
		const existing = this.state.pendingUpdates.get(lineIndex);
		if (existing) clearTimeout(existing);
		const timer = setTimeout(() => {
			this._sync(lineIndex, { _giftwrap: 'true', _giftwrap_message: textarea.value });
			this.state.pendingUpdates.delete(lineIndex);
		}, this.config.debounceMs);
		this.state.pendingUpdates.set(lineIndex, timer);
	},

	/* Cible la ligne par son index (1-based, stable) plutôt que par sa key :
	   Shopify régénère la line item key à chaque mutation, donc un id périmé
	   ferait échouer silencieusement tous les syncs suivant le premier. */
	_sync(lineIndex, properties) {
		return fetch('/cart/change.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
			body: JSON.stringify({ line: parseInt(lineIndex, 10), properties }),
		})
			.then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); })
			.catch((err) => window.log?.('error', '🎁 [CART_GIFTWRAP]', 'Sync failed', { error: err.message }));
	},

	_updateGiftProduct(variantId, quantity) {
		if (quantity > 0) {
			this._addGiftProduct(variantId);
		} else {
			this._removeGiftProduct(variantId);
		}
	},

	_addGiftProduct(variantId) {
		fetch('/cart/add.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
			body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
		})
			.then((r) => r.json().then((data) => {
				if (!r.ok) throw new Error(data.description || `HTTP ${r.status}`);
				window.mmRefreshCartSection?.()
					?.catch((err) => window.log?.('error', '🎁 [CART_GIFTWRAP]', 'UI refresh failed', { error: err.message }));
			}))
			.catch((err) => window.log?.('error', '🎁 [CART_GIFTWRAP]', 'Add gift product failed', { error: err.message }));
	},

	_removeGiftProduct(variantId) {
		const numericId = parseInt(variantId, 10);
		/* /cart/change.js requiert la line key (variantId:hash), pas le variant ID seul */
		fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
			.then((r) => r.json())
			.then((cart) => {
				const line = cart.items.find((i) => i.variant_id === numericId);
				if (!line) return;
				return fetch('/cart/change.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
					body: JSON.stringify({ id: line.key, quantity: 0 }),
				})
					.then((r) => r.json())
					.then((data) => {
						if (data.status >= 400) throw new Error(data.description || `HTTP ${data.status}`);
						window.mmRefreshCartSection?.()
							?.catch((err) => window.log?.('error', '🎁 [CART_GIFTWRAP]', 'UI refresh failed', { error: err.message }));
					});
			})
			.catch((err) => window.log?.('error', '🎁 [CART_GIFTWRAP]', 'Remove gift product failed', { error: err.message }));
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => CartGiftWrapManager.init());
} else {
	CartGiftWrapManager.init();
}

window.MMCartGiftWrap = CartGiftWrapManager;
